import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { localDateFormat } from '../../../utils/timeFormat';
import { isDefaultDate } from '../../../utils/common';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import Loading from '../../Shared/Loading';
import {
    setTransitionalCareFilter,
    setTransitionalCareRecentActivity,
    fetchTransitionalCareReport,
    exportTransitionalCareReport,
} from '../../../store/transitionalCareReport.slice';

const TransitionalCareReport = () => {
    const dispatch = useAppDispatch();

    const { transitionalCareReport, transitionalCareFilter, transitionalCareRecentActivity } = useSelector((state: RootState) => state.transitionalCareReport);
    const { downLoading } = useSelector((state: RootState) => state.shared);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(11);

    // Fixed widths (no flex): flex columns shrink to the viewport so the horizontal scroll extent
    // does not reflect all columns; fixed widths restore full horizontal scrolling.
    const columns: GridColDef[] = [
        {
            field: 'AHCCCSId',
            headerName: 'AHCCCS Id',
            width: 110,
            minWidth: 110,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            width: 160,
            minWidth: 160,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            width: 160,
            minWidth: 160,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            width: 110,
            minWidth: 110,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'Status',
            headerName: 'Status',
            width: 250,
            minWidth: 250,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            width: 200,
            minWidth: 200,
        },
        {
            field: 'Population',
            headerName: 'Population',
            width: 200,
            minWidth: 200,
        },
        {
            field: 'PrimaryProvider',
            headerName: 'Primary Provider',
            width: 200,
            minWidth: 200,
        },
        {
            field: 'TeamName',
            headerName: 'Team',
            width: 200,
            minWidth: 200,
        },
        {
            field: 'TransitionStage',
            headerName: 'Transition Stage',
            width: 180,
            minWidth: 160,
        },
        {
            field: 'TCStatus',
            headerName: 'TC Status',
            width: 220,
            minWidth: 200,
        },
        {
            field: 'AdmissionDate',
            headerName: 'TC Admission Date',
            width: 150,
            minWidth: 140,
            valueGetter: (value, row) =>
                `${isDefaultDate(row.AdmissionDate) ? '-' : localDateFormat(new Date(row.AdmissionDate))}`,
        },
        {
            field: 'LastTransferDate',
            headerName: 'Last Transfer Date',
            width: 150,
            minWidth: 140,
            valueGetter: (value, row) =>
                row.LastTransferDate == null ? '-' : localDateFormat(new Date(row.LastTransferDate)),
        },
        {
            field: 'TeamClOrTccIfFuhfum',
            headerName: 'CL / TCC (FUHFUM)',
            width: 240,
            minWidth: 220,
        },
        {
            field: 'LastSeenInPerson',
            headerName: 'Last Seen In Person',
            width: 150,
            minWidth: 140,
            valueGetter: (value, row) =>
                row.LastSeenInPerson == null ? '-' : localDateFormat(new Date(row.LastSeenInPerson)),
        },
        {
            field: 'CurrentFacilityName',
            headerName: 'Facility Name',
            width: 220,
            minWidth: 200,
        },
        {
            field: 'CurrentFacilityAddress',
            headerName: 'Facility Address',
            width: 260,
            minWidth: 240,
        },
        {
            field: 'FacilityLevel',
            headerName: 'Facility Level',
            width: 170,
            minWidth: 160,
        },
        {
            field: 'CurrentFacility',
            headerName: 'Current Facility',
            width: 240,
            minWidth: 200,
        },
        {
            field: 'DischargeDate',
            headerName: 'Discharge Date',
            width: 150,
            minWidth: 140,
            valueGetter: (value, row) =>
                row.DischargeDate === null ? '-' : `${localDateFormat(new Date(row.DischargeDate))}`,
        },
        {
            field: 'AnticipatedDischargeDate',
            headerName: 'Anticipated DischargeDate',
            width: 200,
            minWidth: 200,
            valueGetter: (value, row) =>
                row.AnticipatedDischargeDate === null ? '-' : `${localDateFormat(new Date(row.AnticipatedDischargeDate))}`,
        },
        {
            field: 'LastActivityNote',
            headerName: 'Last Activity Note',
            width: 350,
            minWidth: 350,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Box style={{ maxHeight: '140px', overflowY: 'scroll' }}>
                        <Typography variant="body2">{params.row.LastActivityNote}</Typography>
                    </Box>
                );
            },
        },
    ];

    useEffect(() => {
        dispatch(fetchTransitionalCareReport(transitionalCareFilter, transitionalCareRecentActivity));
    }, [dispatch, transitionalCareFilter, transitionalCareRecentActivity]);

    return (
        <Grid container>
            <Box width={'100%'} borderBottom={1} paddingBottom={1} marginBottom={1}>
                <Typography fontWeight="fontWeightMedium" component="h3">
                    Transitional Care Report
                </Typography>
            </Box>
            {<Loading message={'Loading...'} />}
            <Grid size={12}>
                <Stack width="100%" direction="row" alignItems="center" marginY={2} className="float-right" spacing={3}>
                    <FormControl sx={{ width: '300px' }}>
                        <InputLabel id="demo-simple-select-label">Filter By</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            defaultValue={transitionalCareFilter}
                            value={transitionalCareFilter}
                            label="Filter By"
                            onChange={(e) => {
                                dispatch(setTransitionalCareFilter(e.target.value));
                            }}
                        >
                            <MenuItem value={'1'}>Current Transitional Care</MenuItem>
                            <MenuItem value={'2'}>Past Transitional Care</MenuItem>
                            <MenuItem value={'3'}>Closed Transitional Care Without NP or BH Visit</MenuItem>
                        </Select>
                    </FormControl>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox onChange={() => dispatch(setTransitionalCareRecentActivity(!transitionalCareRecentActivity))} />}
                            label="Cases with no recent activity"
                        />
                    </FormGroup>

                    <Button
                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                        onClick={() => dispatch(exportTransitionalCareReport(transitionalCareFilter, transitionalCareRecentActivity))}
                        variant="contained"
                        color="success"
                    >
                        {downLoading ? 'Exporting...' : 'Export'}
                    </Button>
                </Stack>
            </Grid>
            <Grid style={{ height: '95vh', width: '100%', minWidth: 0 }}>
                <DataGrid
                    rows={transitionalCareReport}
                    columns={columns}
                    paginationModel={{ pageSize: pageSize, page: page }}
                    pageSizeOptions={[5]}
                    onPaginationModelChange={(model) => {
                        setPageSize(model.pageSize);
                        setPage(model.page);
                    }}
                    getRowId={(row) => row.Id}
                    getRowHeight={() => 'auto'}
                    sx={{
                        width: '100%',
                        [`& .${gridClasses.virtualScroller}`]: {
                            overflowX: 'auto',
                        },
                        [`& .${gridClasses.cell}`]: {
                            py: 1,
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default TransitionalCareReport;
